/**
 * Dates and durations as the interface writes them. English, like the rest of the interface; the
 * absolute form goes in a tooltip, the relative one in the text.
 */
const DATE_TIME = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'medium' });

/** `17 Oct 2026, 10:15:00`; an empty string for a missing value. */
export function dateTime(iso) {
  return iso ? DATE_TIME.format(new Date(iso)) : '';
}

/** `just now`, `42 s ago`, `5 min ago`, `3 h ago`, `2 d ago`; `in 5 min` for the future. */
export function timeAgo(iso, now = Date.now()) {
  if (!iso) {
    return '';
  }
  const seconds = Math.round((now - new Date(iso).getTime()) / 1000);
  const future = seconds < 0;
  const text = duration(Math.abs(seconds));
  if (text === null) {
    return 'just now';
  }
  return future ? `in ${text}` : `${text} ago`;
}

/** A duration in seconds, coarsened to its largest unit; null under five seconds. */
export function duration(seconds) {
  if (seconds < 5) {
    return null;
  }
  if (seconds < 60) {
    return `${seconds} s`;
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} min`;
  }
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)} h`;
  }
  return `${Math.floor(seconds / 86400)} d`;
}

/** `sha256:9f2c…a1b3`: enough of a digest to compare two by eye. */
export function shortDigest(digest) {
  if (!digest) {
    return '';
  }
  const [algorithm, hex] = digest.includes(':') ? digest.split(':') : ['', digest];
  const short = hex.length > 12 ? `${hex.slice(0, 6)}…${hex.slice(-4)}` : hex;
  return algorithm ? `${algorithm}:${short}` : short;
}

/**
 * A revision named by an instance: `#7`. A clawlet can report an applied revision the plane does
 * not know; it has no number, and is written with the start of its id.
 */
export function revisionLabel(ref) {
  if (!ref) {
    return '—';
  }
  return ref.number == null ? `unknown ${ref.id.slice(0, 8)}` : `#${ref.number}`;
}

/**
 * `studio-rossi` from `Studio Rossi`: lowercase letters, digits and `-`, neither at the start nor
 * at the end, at most `maxLength` characters. Accents go, other characters become `-`. It
 * suggests an id from a name; the id is still checked where it is typed.
 */
export function slug(text, maxLength) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, maxLength)
    .replace(/-+$/, '');
}

/**
 * Wires the id field of a form to its name field: while the id was not typed, it is the slug of
 * the name. Emptying the id makes it follow the name again.
 */
export const idFollowsName = {
  /** On input in the name field. */
  name(event, maxLength) {
    const id = event.target.form.elements.id;
    if (!id.dataset.typed) {
      id.value = slug(event.target.value, maxLength);
    }
  },
  /** On input in the id field. */
  id(event) {
    if (event.target.value) {
      event.target.dataset.typed = 'true';
    } else {
      delete event.target.dataset.typed;
    }
  },
  /** After the form is reset. */
  reset(form) {
    delete form.elements.id.dataset.typed;
  },
};

/** The most recent of some image versions (`2026.10.8` before `2026.10.10`); undefined for none. */
export function newestVersion(versions) {
  return [...(versions ?? [])].sort((a, b) => a.localeCompare(b, 'en', { numeric: true })).at(-1);
}
